import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, BrainCircuit, Check, ChevronDown, ChevronUp, Minus, ShieldAlert, X, Zap } from "lucide-react";
import { QUAL_CONFIG, bandFor, computeQual } from "@/lib/qualification";
import { impactLinkForCase } from "@/lib/caseLinks";
import { OPS_CONFIG, SIGNAL_SOURCES, POLICY_CONFIG, VALIDATION, POLICY_HEALTH, MEASUREMENT_MODE, DEDUP_NOTE, CAPACITY } from "@/lib/opsIntel";

const CLASS_TONE = {
  STANDARD: "text-zinc-300 border-zinc-600 bg-zinc-800/60",
  PRIORITY: "text-sky-400 border-sky-500/40 bg-sky-500/10",
  VIP: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
  HEALTHY: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
  WATCH: "text-amber-400 border-amber-500/40 bg-amber-500/10",
  "RETENTION PRIORITY": "text-orange-400 border-orange-500/40 bg-orange-500/10",
  CRITICAL: "text-red-400 border-red-500/40 bg-red-500/10",
};

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 44);

const DeltaChip = ({ d, testId }) => {
  if (d > 0) return <span data-testid={testId} className="font-mono text-[10px] font-semibold text-emerald-400">↑ +{d}</span>;
  if (d < 0) return <span data-testid={testId} className="font-mono text-[10px] font-semibold text-red-400">↓ {d}</span>;
  return <span data-testid={testId} className="font-mono text-[10px] text-zinc-500 flex items-center gap-0.5"><Minus className="w-2.5 h-2.5" /> No change</span>;
};

const GroupLabel = ({ children }) => (
  <div className="text-[9px] font-mono tracking-widest text-emerald-500/80 uppercase mb-1.5 mt-1">{children}</div>
);

const CHIP_TONES = {
  HIGH: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
  MEDIUM: "text-amber-400 border-amber-500/40 bg-amber-500/10",
  LOW: "text-red-400 border-red-500/40 bg-red-500/10",
  UNKNOWN: "text-zinc-400 border-zinc-700 bg-zinc-800/60",
  ELIGIBLE: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
  "REVIEW REQUIRED": "text-amber-400 border-amber-500/40 bg-amber-500/10",
  RESTRICTED: "text-red-400 border-red-500/40 bg-red-500/10",
  HOT: "text-orange-400 border-orange-500/40 bg-orange-500/10",
  WARM: "text-amber-400 border-amber-500/40 bg-amber-500/10",
  COOLING: "text-sky-400 border-sky-500/40 bg-sky-500/10",
  STALE: "text-zinc-400 border-zinc-700 bg-zinc-800/60",
  FRESH: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
  AGING: "text-amber-400 border-amber-500/40 bg-amber-500/10",
  IMPROVING: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
  DECLINING: "text-red-400 border-red-500/40 bg-red-500/10",
  STABLE: "text-zinc-300 border-zinc-600 bg-zinc-800/60",
  BREACH: "text-red-400 border-red-500/50 bg-red-500/10",
};

const chipTone = (k) => CHIP_TONES[k] || "text-zinc-300 border-zinc-700 bg-zinc-800/60";

const fmtClock = (s) => `${Math.floor(Math.abs(s) / 60)}:${String(Math.abs(s) % 60).padStart(2, "0")}`;

export const IntelligencePanel = ({ sc, step, breach, spotlight, rank, queueSize }) => {
  const cfg = QUAL_CONFIG[sc.id];
  const qual = useMemo(() => computeQual(sc.id, sc.timeline, step, { breach }), [sc, step, breach]);
  const latest = useRef(qual);
  latest.current = qual;
  const shownRef = useRef(qual);
  const shownId = useRef(sc.id);
  const first = useRef(true);
  const [shown, setShown] = useState(qual);
  const [delta, setDelta] = useState({ score: 0, priority: 0 });
  const [recalc, setRecalc] = useState(false);
  const [open, setOpen] = useState(false);
  const [signalsOpen, setSignalsOpen] = useState(false);
  const watchKey = `${sc.id}:${step}:${breach}`;

  useEffect(() => {
    if (first.current) { first.current = false; return; }
    if (shownId.current !== sc.id) {
      shownId.current = sc.id;
      shownRef.current = latest.current;
      setShown(latest.current);
      setDelta({ score: 0, priority: 0 });
      setRecalc(false);
      return;
    }
    setRecalc(true);
    const t = setTimeout(() => {
      const oldQ = shownRef.current;
      const newQ = latest.current;
      setDelta({ score: newQ.score - oldQ.score, priority: newQ.priority - oldQ.priority });
      shownRef.current = newQ;
      setShown(newQ);
      setRecalc(false);
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchKey]);

  const intel = cfg?.intel || null;
  const ops = OPS_CONFIG[sc.id] || null;
  const complete = cfg ? step >= sc.timeline.length - 1 : false;
  const [elapsed, setElapsed] = useState(0);
  const [slaElapsed, setSlaElapsed] = useState(0);
  const [overridden, setOverridden] = useState(false);
  const [overrideTs, setOverrideTs] = useState(null);
  const decayOn = Boolean(complete && intel?.decay);
  const slaCfg = intel?.sla || null;
  const slaActive = Boolean(slaCfg && step >= slaCfg.startStep);

  useEffect(() => {
    setElapsed(0);
    if (!decayOn) return;
    const started = Date.now();
    const iv = setInterval(() => setElapsed(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decayOn, sc.id]);

  useEffect(() => {
    setSlaElapsed(0);
    if (!slaActive) return;
    const started = Date.now();
    const iv = setInterval(() => setSlaElapsed(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slaActive, sc.id]);

  useEffect(() => { setOverridden(false); setOverrideTs(null); }, [sc.id]);

  if (!cfg || !shown) return null;

  const decayStage = intel?.decay ? [...intel.decay].reverse().find((d) => elapsed >= d.after) || null : null;
  const effScore = Math.max(0, shown.score + (decayStage?.delta || 0));
  const effClass = decayStage ? bandFor(shown.kind, effScore) : shown.classification;
  const windowLabel = decayStage ? decayStage.window : shown.window;
  const slaRemaining = slaCfg ? slaCfg.minutes * 60 - slaElapsed : 0;

  const isClient = shown.kind === "client";
  const base = cfg.base;
  const ticks = [
    ["Classification", shown.classification !== bandFor(shown.kind, base.score)],
    ["Dynamic score", shown.score !== base.score],
    ["Reason codes", shown.codes.length > (base.codes || []).length],
    ["Next Best Action", shown.nba !== base.nba],
    ["Priority level", shown.priority !== base.priority],
  ];
  const dirty = ticks.some(([, v]) => v);
  const payload = {
    classification: shown.classification.toLowerCase(),
    [isClient ? "value_engagement_score" : "intent_score"]: shown.score,
    action_priority: shown.priority,
    reason_codes: shown.codes,
    next_best_action: slug(shown.nba),
  };

  return (
    <div
      className={`bg-zinc-900 border rounded-md p-5 transition-shadow duration-500 ${spotlight ? "border-emerald-400 shadow-[0_0_0_3px_rgba(16,185,129,0.3),0_0_30px_rgba(16,185,129,0.25)]" : "border-emerald-500/25 shadow-[0_0_15px_rgba(16,185,129,0.06)]"}`}
      data-testid="intelligence-panel"
      data-intel-kind={shown.kind}
      data-intel-scenario={sc.id}
    >
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="text-[10px] font-mono tracking-widest text-emerald-400 uppercase flex items-center gap-1.5">
          <BrainCircuit className="w-3.5 h-3.5" strokeWidth={1.5} />
          {isClient ? "Client Intelligence" : "Live Intelligence"}
        </div>
        <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-mono font-semibold border transition-colors duration-500 ${CLASS_TONE[effClass]}`} data-testid="intel-classification">
          {effClass}
        </span>
        <span className="ml-auto text-[9px] font-mono tracking-widest" data-testid="intel-status-line">
          {recalc
            ? <span className="text-amber-400 animate-pulse">SIGNAL RECEIVED — INTELLIGENCE RECALCULATING…</span>
            : dirty
              ? <span className="text-emerald-500">CRM UPDATED ✓</span>
              : <span className="text-zinc-500">LISTENING FOR SIGNALS</span>}
        </span>
      </div>

      {(cfg.arrival || cfg.clientContext) && (
        <div className="mb-4 border border-zinc-800 rounded-sm bg-zinc-950/50 p-3.5" data-testid={cfg.arrival ? "arrival-intelligence" : "client-context"}>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="text-[9px] font-mono tracking-widest text-zinc-500 uppercase">
              {cfg.arrival ? "Arrival Intelligence — initial qualification" : "Client Context — baseline"}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-[9px] font-mono font-semibold border ${CLASS_TONE[bandFor(shown.kind, base.score)]}`} data-testid="arrival-classification">
              {bandFor(shown.kind, base.score)} {base.score}/100
            </span>
          </div>
          {cfg.arrival && (
            <>
              <div className="text-[9px] font-mono tracking-widest text-zinc-600 uppercase mb-1.5">Why this lead started here</div>
              <div className="space-y-1 mb-2" data-testid="arrival-contributions">
                {cfg.arrival.contributions.map((c) => (
                  <div key={c.label} className="flex items-baseline gap-2 text-[10px] font-mono">
                    <span className="text-zinc-500 shrink-0 w-28 sm:w-32 truncate">{c.label}</span>
                    <span className="text-zinc-400 flex-1 min-w-0">{c.detail}</span>
                    <span className={`shrink-0 ${c.pts > 0 ? "text-emerald-400" : "text-zinc-600"}`}>{c.pts > 0 ? `+${c.pts}` : c.pts}</span>
                  </div>
                ))}
              </div>
              <div className="text-[9px] font-mono text-amber-500/90 mb-2" data-testid="arrival-priority-note">
                Initial Action Priority {base.priority}/100 — {cfg.arrival.priorityNote}
              </div>
              <button
                data-testid="arrival-signals-toggle"
                onClick={() => setSignalsOpen(!signalsOpen)}
                className="flex items-center gap-1.5 text-[9px] font-mono tracking-wider text-zinc-500 hover:text-emerald-400 transition-colors duration-150"
              >
                {signalsOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {signalsOpen ? "HIDE ARRIVAL SIGNALS" : "VIEW ARRIVAL SIGNALS"}
              </button>
              {signalsOpen && (
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1.5 fade-slide-in" data-testid="arrival-signals">
                  {cfg.arrival.meta.map(([k, v]) => (
                    <div key={k} className="min-w-0"><span className="text-[9px] font-mono text-zinc-600">{k}: </span><span className="text-[10px] font-mono text-zinc-300">{v}</span></div>
                  ))}
                </div>
              )}
              <div className="text-[8px] font-mono text-zinc-600 mt-2 tracking-wider">
                ARRIVAL SIGNALS → INITIAL QUALIFICATION · LIVE SIGNALS → DYNAMIC REQUALIFICATION · SIMULATED DATA
              </div>
            </>
          )}
          {cfg.clientContext && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1.5" data-testid="client-context-fields">
              {cfg.clientContext.fields.map(([k, v]) => (
                <div key={k} className="min-w-0"><span className="text-[9px] font-mono text-zinc-600">{k}: </span><span className="text-[10px] font-mono text-zinc-300">{v}</span></div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <div className="flex items-baseline justify-between mb-1.5">
            <span className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">{isClient ? "Client Value / Engagement" : "Intent / Value"}</span>
            <DeltaChip d={delta.score} testId="intel-score-delta" />
          </div>
          <div className={`text-2xl font-bold font-mono transition-colors duration-500 ${recalc ? "text-emerald-300 animate-pulse" : "text-zinc-100"}`} data-testid="intel-score">
            {effScore}<span className="text-sm text-zinc-500">/100</span>
          </div>
          <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden mt-1.5">
            <div className="h-full rounded-full bg-emerald-500 transition-all duration-700" style={{ width: `${shown.score}%` }} />
          </div>
        </div>
        <div>
          <div className="flex items-baseline justify-between mb-1.5">
            <span className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">{isClient ? "Intervention Priority" : "Action Priority"}</span>
            <DeltaChip d={delta.priority} testId="intel-priority-delta" />
          </div>
          <div className={`text-2xl font-bold font-mono transition-colors duration-500 ${recalc ? "text-amber-300 animate-pulse" : "text-zinc-100"}`} data-testid="intel-priority">
            {shown.priority}<span className="text-sm text-zinc-500">/100</span>
          </div>
          <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden mt-1.5">
            <div className="h-full rounded-full bg-amber-400 transition-all duration-700" style={{ width: `${shown.priority}%` }} />
          </div>
        </div>
      </div>

      {intel && (
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="intel-chips">
          {rank && (
            <span data-testid="intel-rank" className="px-2 py-1 rounded-sm border text-[9px] font-mono font-semibold tracking-wider text-emerald-400 border-emerald-500/40 bg-emerald-500/10">
              PRIORITY RANK #{rank} / {queueSize}
            </span>
          )}
          {ops?.freshness && (
            <span data-testid="intel-freshness" className={`px-2 py-1 rounded-sm border text-[9px] font-mono font-semibold tracking-wider ${chipTone(ops.freshness.level)}`}>
              FRESHNESS {ops.freshness.level} · {ops.freshness.registered}
            </span>
          )}
          <span data-testid="intel-confidence" className={`px-2 py-1 rounded-sm border text-[9px] font-mono font-semibold tracking-wider ${chipTone(intel.confidence.level)}`}>
            CONFIDENCE {intel.confidence.level} · SIGNALS {intel.confidence.available}/{intel.confidence.total}
          </span>
          {windowLabel && (
            <span data-testid="intel-window" className={`px-2 py-1 rounded-sm border text-[9px] font-mono font-semibold tracking-wider ${chipTone(windowLabel)}`}>
              OPPORTUNITY WINDOW: {windowLabel}
            </span>
          )}
          {slaActive && (
            <span data-testid="intel-sla" className={`px-2 py-1 rounded-sm border text-[9px] font-mono font-semibold tracking-wider ${slaRemaining < 0 ? CHIP_TONES.BREACH : CHIP_TONES.WARM}`}>
              ACTION SLA — {slaCfg.label}: {slaRemaining >= 0 ? `${fmtClock(slaRemaining)} remaining` : `BREACHED +${fmtClock(slaRemaining)}`}
            </span>
          )}
          {decayStage && (
            <span data-testid="intel-decay-note" className="text-[9px] font-mono text-zinc-500">
              {decayStage.note} — {fmtClock(elapsed)} since last signal
            </span>
          )}
        </div>
      )}

      {shown.statement && (
        <div className="mb-4 border-l-2 border-sky-500/60 bg-sky-500/5 rounded-sm px-3 py-2 text-[11px] text-sky-300 leading-relaxed" data-testid="intel-statement">
          {shown.statement}
        </div>
      )}

      <div className="mb-4">
        <div className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-2">Why this changed</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5" data-testid="intel-reasons">
          {shown.reasons.map((r) => (
            <div key={r} className="flex items-start gap-2 text-[11px] text-zinc-300">
              <Zap className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" strokeWidth={1.5} /> {r}
            </div>
          ))}
        </div>
      </div>

      <div className="mb-4 border-l-2 border-emerald-500/60 bg-emerald-500/5 rounded-sm px-3 py-2.5" data-testid="intel-nba">
        <div className="text-[9px] font-mono tracking-widest text-emerald-500 uppercase mb-1">Next Best Action</div>
        <div className="text-xs font-semibold text-zinc-100">{shown.nba}</div>
      </div>

      {ops?.whyNow && (
        <div className="mb-4 border-l-2 border-amber-500/60 bg-amber-500/5 rounded-sm px-3 py-2.5" data-testid="intel-why-now">
          <div className="text-[9px] font-mono tracking-widest text-amber-500 uppercase mb-1">Why this lead, why now?</div>
          <div className="text-xs text-zinc-200 leading-relaxed">{ops.whyNow}</div>
          <div className="text-[10px] font-mono text-zinc-400 mt-1.5" data-testid="intel-routing">
            BEST CHANNEL: <span className="text-zinc-200">{ops.bestChannel.channel}</span> · TEAM: <span className="text-zinc-200">{ops.routing.team}</span> · WHEN: <span className="text-zinc-200">{ops.bestTime}</span>
          </div>
        </div>
      )}

      {shown.governance && (
        <div className="mb-4 border border-amber-500/30 bg-amber-500/5 rounded-sm p-3.5" data-testid="intel-governance">
          <div className="flex items-center gap-2 text-[10px] font-mono tracking-widest text-amber-400 uppercase mb-2">
            <ShieldAlert className="w-3.5 h-3.5" strokeWidth={1.5} /> {shown.governance.title}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 mb-2">
            <div>
              {shown.governance.allowed.map((a) => (
                <div key={a} className="flex items-start gap-1.5 text-[10px] text-zinc-300"><Check className="w-3 h-3 text-emerald-400 mt-0.5 shrink-0" /> {a}</div>
              ))}
            </div>
            <div>
              {shown.governance.notAllowed.map((a) => (
                <div key={a} className="flex items-start gap-1.5 text-[10px] text-zinc-400"><X className="w-3 h-3 text-red-400 mt-0.5 shrink-0" /> {a}</div>
              ))}
            </div>
          </div>
          <div className="text-[10px] font-mono text-amber-500/90">{shown.governance.note}</div>
        </div>
      )}

      <div className="border border-zinc-800 rounded-sm bg-zinc-950/60 px-3.5 py-2.5 mb-3" data-testid="intel-crm-status">
        <div className="text-[9px] font-mono tracking-widest text-zinc-500 uppercase mb-1.5">CRM Status — write-back</div>
        <div className="flex flex-wrap gap-x-5 gap-y-1">
          {ticks.map(([label, doneTick]) => (
            <span key={label} className={`flex items-center gap-1.5 text-[10px] font-mono ${doneTick ? "text-emerald-400" : "text-zinc-600"}`}>
              {label} {doneTick ? "✓" : "·"}
            </span>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button
          data-testid="intel-expand-toggle"
          onClick={() => setOpen(!open)}
          className="flex items-center gap-1.5 text-[10px] font-mono tracking-wider text-zinc-500 hover:text-emerald-400 transition-colors duration-150"
        >
          {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          {open ? "HIDE SCORING DETAIL" : "VIEW SCORING LOGIC & CRM DETAIL"}
        </button>
        {impactLinkForCase(sc.id) && (
          <Link
            to={impactLinkForCase(sc.id)}
            data-testid="view-measured-impact"
            className="inline-flex items-center gap-1.5 text-[10px] font-mono font-semibold tracking-wider text-emerald-400 hover:text-emerald-300 transition-colors duration-150"
          >
            VIEW MEASURED IMPACT <ArrowUpRight className="w-3 h-3" strokeWidth={2} />
          </Link>
        )}
      </div>

      {open && (
        <div className="mt-3 pt-3 border-t border-zinc-800 space-y-4 fade-slide-in" data-testid="intel-detail">
          <div>
            <GroupLabel>Commercial Intelligence</GroupLabel>
            {ops && (
              <>
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-[10px] font-mono text-zinc-400 mb-1.5" data-testid="intel-propensity">
                  <span>Conversion Propensity: <span className="text-zinc-200">{ops.propensity != null ? `${ops.propensity}%` : "— (existing client)"}</span></span>
                  <span>Value Potential: <span className="text-zinc-200">{ops.valuePotential}</span></span>
                  <span>Expected Opportunity: <span className="text-emerald-400">{ops.expectedOpportunity}</span></span>
                </div>
                <div className="text-[9px] font-mono text-zinc-600 mb-2">A high-probability lead is not always the highest-value opportunity — propensity and value are measured separately.</div>
              </>
            )}
            {intel?.source && (
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] font-mono mb-2" data-testid="intel-source-trend">
                <span className="text-zinc-300">{intel.source.name}</span>
                <span className="text-zinc-600">— {intel.source.basis}:</span>
                <span className={`px-1.5 py-0.5 rounded-sm border text-[9px] font-semibold ${chipTone(intel.source.quality)}`}>{intel.source.quality}</span>
                <span className={`px-1.5 py-0.5 rounded-sm border text-[9px] font-semibold ${chipTone(intel.source.trend)}`}>TREND {intel.source.trend}</span>
              </div>
            )}
            <div className="text-[9px] font-mono tracking-widest text-zinc-500 uppercase mb-1.5">Reason codes</div>
            <div className="flex flex-wrap gap-1.5" data-testid="intel-reason-codes">
              {shown.codes.map((c) => (
                <span key={c} className="px-2 py-0.5 rounded-sm text-[9px] font-mono border border-zinc-800 bg-zinc-900 text-zinc-400">{c} · {SIGNAL_SOURCES[c] || "CRM"}</span>
              ))}
            </div>
          </div>
          <div>
            <GroupLabel>Operational Intelligence</GroupLabel>
            {ops && (
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-[10px] font-mono text-zinc-400 mb-2" data-testid="intel-ops-detail">
                {rank && <span>Queue Rank: <span className="text-emerald-400">#{rank} / {queueSize}</span></span>}
                <span>Freshness: <span className="text-zinc-200">{ops.freshness.level}</span> ({ops.freshness.registered})</span>
                {windowLabel && <span>Opportunity Window: <span className="text-zinc-200">{windowLabel}</span></span>}
                <span>Best Contact Window: <span className="text-zinc-200">{ops.bestTime}</span></span>
                <span>Best Channel: <span className="text-zinc-200">{ops.bestChannel.channel}</span></span>
                <span>Routing: <span className="text-zinc-200">{ops.routing.team} · {ops.routing.agent}</span></span>
                <span>Capacity: <span className="text-zinc-200">{CAPACITY.agents} agents available — immediate routing by queue position</span></span>
              </div>
            )}
            {ops && (
              <div className="space-y-0.5 mb-2">
                {ops.bestChannel.reasons.map((r) => (
                  <div key={r} className="text-[10px] font-mono text-zinc-500">• channel signal: {r}</div>
                ))}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2 mb-1.5" data-testid="intel-contactability">
              <span className={`px-1.5 py-0.5 rounded-sm border text-[9px] font-mono font-semibold ${chipTone(intel.contactability.level)}`}>
                CONTACTABILITY {intel.contactability.level}
              </span>
            </div>
            <div className="space-y-0.5 mb-1">
              {intel.contactability.signals.map((s) => (
                <div key={s} className="text-[10px] font-mono text-zinc-400">• {s}</div>
              ))}
            </div>
            {slaCfg && (
              <div className="text-[10px] font-mono text-zinc-400" data-testid="intel-sla-detail">
                ACTION SLA — {slaCfg.label}: contact within {slaCfg.minutes} minutes
                {slaActive ? (slaRemaining >= 0 ? ` — ${fmtClock(slaRemaining)} remaining` : ` — BREACHED +${fmtClock(slaRemaining)}`) : " — starts when human action is required"}
              </div>
            )}
          </div>
          <div>
            <GroupLabel>Identity / Acquisition</GroupLabel>
            {ops && (
              <div className="space-y-0.5" data-testid="intel-identity">
                <div className="text-[10px] font-mono text-zinc-400">Identity: <span className="text-zinc-200">{ops.identity.status}</span></div>
                {ops.identity.signals?.map((s) => (
                  <div key={s} className="text-[10px] font-mono text-zinc-500">— matched signal: {s}</div>
                ))}
                {ops.identity.action && <div className="text-[10px] font-mono text-emerald-400/90">Recommendation: {ops.identity.action}</div>}
                {ops.sourceHealth && (
                  <div className="text-[10px] font-mono text-zinc-400" data-testid="intel-source-health">
                    Source health today: <span className={ops.sourceHealth.today === "ANOMALOUS" ? "text-amber-400" : "text-emerald-400"}>{ops.sourceHealth.today}</span> — {ops.sourceHealth.note}
                  </div>
                )}
                {ops.economics && (
                  <div className="text-[10px] font-mono text-zinc-400" data-testid="intel-economics">
                    Acquisition economics: source cost {ops.economics.sourceCost} · historical efficiency {ops.economics.efficiency} — context for prioritisation, never a reason to change client treatment
                  </div>
                )}
              </div>
            )}
          </div>
          <div>
            <GroupLabel>Governance / Risk</GroupLabel>
            <div className="flex flex-wrap items-center gap-2 mb-1.5">
              <span data-testid="intel-eligibility" className={`px-1.5 py-0.5 rounded-sm border text-[9px] font-mono font-semibold ${chipTone(intel.eligibility.status)}`}>
                ELIGIBILITY: {intel.eligibility.status}
              </span>
              <span data-testid="intel-risk" className={`px-1.5 py-0.5 rounded-sm border text-[9px] font-mono font-semibold ${chipTone(intel.risk.level)}`}>
                RISK REVIEW: {intel.risk.level}
              </span>
            </div>
            <div className="text-[10px] font-mono text-zinc-400 mb-0.5">{intel.eligibility.note}</div>
            <div className="text-[10px] font-mono text-zinc-400 mb-1.5">{intel.risk.note}</div>
            {ops?.conflictNote && (
              <div className="text-[10px] font-mono text-sky-300 mb-1.5" data-testid="intel-conflict">{ops.conflictNote}</div>
            )}
            {ops?.permissions && (
              <div className="mb-1.5" data-testid="intel-permissions">
                <div className="text-[9px] font-mono tracking-widest text-zinc-500 uppercase mb-1">Communication Permission</div>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(ops.permissions).map(([ch, st]) => (
                    <span key={ch} className={`px-1.5 py-0.5 rounded-sm border text-[9px] font-mono ${st === "Allowed" ? "border-emerald-500/30 text-emerald-400" : "border-red-500/30 text-red-400"}`}>{ch}: {st}</span>
                  ))}
                </div>
                {ops.permissionsNote && <div className="text-[9px] font-mono text-amber-500/90 mt-1">{ops.permissionsNote}</div>}
                <div className="text-[9px] font-mono text-zinc-600 mt-1">Commercial priority never overrides communication permissions.</div>
              </div>
            )}
            {ops?.contactHistory && (
              <div className="text-[10px] font-mono text-zinc-400 mb-1.5" data-testid="intel-contact-history">
                Contact history: {ops.contactHistory.attempts} attempts · {ops.contactHistory.window} → <span className={ops.contactHistory.status === "CONTACT NOW" ? "text-emerald-400" : "text-amber-400"}>{ops.contactHistory.status}</span>
                {ops.contactHistory.note ? ` — ${ops.contactHistory.note}` : ""}
              </div>
            )}
            {intel.override?.restricted ? (
              <div className="text-[10px] font-mono text-red-400/90" data-testid="override-restricted">
                Human override unavailable — {intel.override.note}
              </div>
            ) : intel.override && !overridden ? (
              <button
                data-testid="human-override-button"
                onClick={() => { setOverridden(true); setOverrideTs(new Date().toLocaleTimeString("en-GB")); }}
                className="px-2.5 py-1.5 rounded-sm border border-zinc-700 text-[9px] font-mono font-semibold tracking-wider text-zinc-400 hover:text-amber-300 hover:border-amber-500/40 transition-colors duration-150"
              >
                SIMULATE HUMAN OVERRIDE
              </button>
            ) : intel.override && overridden ? (
              <div data-testid="override-applied" className="text-[10px] font-mono text-amber-300 border border-amber-500/30 bg-amber-500/5 rounded-sm px-2.5 py-1.5">
                Overridden by {intel.override.by} → new operational priority {intel.override.to} · Reason: {intel.override.reason} · {overrideTs}
              </div>
            ) : null}
          </div>
          <div>
            <GroupLabel>Decision Quality</GroupLabel>
            <div className="flex flex-wrap items-center gap-2 mb-1.5" data-testid="intel-confidence-detail">
              <span className={`px-1.5 py-0.5 rounded-sm border text-[9px] font-mono font-semibold ${chipTone(intel.confidence.level)}`}>
                CONFIDENCE {intel.confidence.level}
              </span>
              <span className="text-[10px] font-mono text-zinc-400">
                Signals available {intel.confidence.available}/{intel.confidence.total}
              </span>
            </div>
            {intel.confidence.missing.length > 0 ? (
              <div className="space-y-0.5" data-testid="intel-missing-signals">
                {intel.confidence.missing.map((m) => (
                  <div key={m} className="text-[10px] font-mono text-amber-500/80">— {m}</div>
                ))}
              </div>
            ) : (
              <div className="text-[10px] font-mono text-zinc-500" data-testid="intel-missing-signals">All key signals available</div>
            )}
          </div>
          <div>
            <GroupLabel>Traceability</GroupLabel>
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-[10px] font-mono text-zinc-400 mb-1.5" data-testid="intel-trace">
              <span>Decision ID: <span className="text-emerald-400">{intel.trace.id}</span></span>
              <span>Scoring Policy: {intel.trace.policy}</span>
              <span>Decision Time: {shown.history[shown.history.length - 1].t}</span>
            </div>
            <div className="space-y-0.5 mb-1" data-testid="audit-trail">
              <div className="text-[10px] font-mono text-zinc-400">
                SYSTEM DECISION — {effClass} {effScore}/100 · {shown.codes[shown.codes.length - 1] || "baseline"} · {shown.history[shown.history.length - 1].t}
              </div>
              {overridden && intel.override && !intel.override.restricted && (
                <div className="text-[10px] font-mono text-amber-300">
                  HUMAN OVERRIDE — {intel.override.by} → {intel.override.to} · {intel.override.reason} · {overrideTs}
                </div>
              )}
              <div className="text-[10px] font-mono text-zinc-500">CRM WRITE-BACK — classification · score · reason codes · NBA ✓</div>
            </div>
            <div className="text-[10px] font-mono text-zinc-500 mt-1" data-testid="intel-policy-config">
              Policy Configuration: {POLICY_CONFIG[shown.kind]} — broker-specific thresholds, not universal defaults
            </div>
            <div className="text-[10px] font-mono text-zinc-600">{DEDUP_NOTE}</div>
          </div>
          <div>
            <div className="text-[9px] font-mono tracking-widest text-zinc-500 uppercase mb-1.5">Qualification history</div>
            <div className="space-y-1" data-testid="intel-history">
              {shown.history.map((h, i) => (
                <div key={`${h.t}-${i}`}>
                  <div className="flex items-center gap-2 text-[10px] font-mono">
                    <span className="text-zinc-600 w-14 shrink-0">{h.t}</span>
                    <span className="text-zinc-400 flex-1 truncate">{h.label}</span>
                    <span className="text-zinc-500 shrink-0 hidden sm:inline">{h.classification}</span>
                    <span className="text-emerald-400 shrink-0 w-8 text-right">{h.score}</span>
                  </div>
                  {i === 0 && cfg.arrival && (
                    <div className="ml-16 mt-0.5 mb-1 space-y-0.5" data-testid="history-arrival-signals">
                      {cfg.arrival.contributions.map((c) => (
                        <div key={c.label} className="text-[9px] font-mono text-zinc-600">• {c.label} ({c.pts > 0 ? `+${c.pts}` : c.pts})</div>
                      ))}
                    </div>
                  )}
                  {i === 0 && cfg.clientContext && (
                    <div className="ml-16 mt-0.5 mb-1 text-[9px] font-mono text-zinc-600">• Baseline from client context — tenure, funding history, activity baseline, relationship value</div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[9px] font-mono tracking-widest text-zinc-500 uppercase mb-1.5">CRM write-back payload</div>
            <pre className="text-[10px] font-mono text-emerald-300/90 bg-zinc-950 border border-zinc-800 rounded-sm p-3 overflow-x-auto" data-testid="intel-json">{JSON.stringify(payload, null, 2)}</pre>
          </div>
          <div>
            <GroupLabel>Measurement</GroupLabel>
            <div className="space-y-0.5 text-[10px] font-mono text-zinc-400" data-testid="intel-measurement">
              <div>Measurement mode: {MEASUREMENT_MODE}</div>
              <div>Historical validation: policy tested against {VALIDATION.leads} simulated leads — {VALIDATION.result}</div>
              <div className="text-zinc-600">{VALIDATION.note}</div>
              <div>Model / policy health: <span className="text-emerald-400">{POLICY_HEALTH.status}</span> — {POLICY_HEALTH.note}</div>
            </div>
          </div>
          <div data-testid="intel-feedback">
            <GroupLabel>Outcome Feedback</GroupLabel>
            <div className="text-[10px] font-mono text-zinc-400">
              Prediction: {intel.feedback.prediction} → Outcome: {intel.feedback.outcome}
            </div>
            <div className="text-[10px] font-mono text-emerald-400/90">{intel.feedback.note}</div>
          </div>
          <div className="text-[9px] font-mono text-zinc-600 leading-relaxed border-t border-zinc-800 pt-3">
            DECISION INTELLIGENCE LAYER — Traditional CRM: lead received → static score. O.N.E.Tech: signal → interpret → re-evaluate → classify → explain → act / escalate → write intelligence back to CRM. The CRM remains the system of record.
          </div>
        </div>
      )}
    </div>
  );
};
